import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { AppConfig } from '~/core/models/appconfig';

// Priority order: Try browser/assets/config.json first (deployment path for all envs)
// Fallback to assets/config.json if browser path doesn't exist
const FILES = ["browser/assets/config.json", "assets/config.json"];
const LOCAL_FILE = 'assets/setlocal.json';

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  private config: AppConfig = new AppConfig();
  private http: HttpClient;

  constructor(private httpHandler: HttpBackend) {
    this.http = new HttpClient(this.httpHandler);
  }

  public async init(): Promise<boolean> {
    try {
      let configData: AppConfig | null = null;

      // Try each config file path until one succeeds
      // Priority: browser/assets/config.json (deployment), then assets/config.json (dev fallback)
      for (const file of FILES) {
        try {
          const config$ = this.http.get<AppConfig>(file);
          configData = await lastValueFrom(config$);
          break; // Success, exit loop
        } catch (e) {
          console.warn(`Failed to load config from ${file}, trying next...`);
        }
      }

      if (!configData) {
        throw new Error('Failed to load config from any path');
      }

      // Try to load local override config (optional, for local dev)
      let localConfig: Partial<AppConfig> | null = null;
      try {
        const local$ = this.http.get<Partial<AppConfig>>(LOCAL_FILE);
        localConfig = await lastValueFrom(local$);
        console.log('Loaded local config override:', localConfig);
      } catch (e) {
        // Local config is optional
      }

      // Merge local config if available
      this.config = { ...configData, ...(localConfig || {}) };
      return true;
    } catch (error) {
      console.error('Error loading config:', error);
      throw error;
    }
  }

  public getConfig(key?: string | string[]): any {
    if (!key || (Array.isArray(key) && !key[0])) {
      return this.config;
    }

    const path = Array.isArray(key) ? key : key.split('.');
    return path.reduce((acc: any, current: string) => acc?.[current], this.config);
  }
}